import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Box,
    SimpleGrid,
    Heading,
    useBreakpointValue,
    Flex,
    VStack,
  } from '@chakra-ui/react'
  import React from 'react';
  import { ImFilePdf } from 'react-icons/im';
  import {isHiddenByRule} from "../../lib/form-rules/generated/fieldVisibility";

export default function ViewCompletedFrom({form, close}){
    const layoutPx = useBreakpointValue({base: 3, md: 4, lg: 8});
    const layoutPy = useBreakpointValue({base: 4, md: 6, lg: 8});
    const contentMaxW = useBreakpointValue({base: "full", md: "56rem", lg: "60rem"});

    function renderElement(obj) {
      const {element, key, props = {}, children} = obj;
  
      if (key == "Signature") {
        const label = children?.[0] || "";
        const signature = children?.[1]?.children || "";

        return <Signature label={label} signature={signature} />;
      }

      // Payment Method: the saved value is a {saved,last4,brand} object — never
      // render it as a child. Show a read-only chip instead.
      if (key == "payment-method") {
        const labelObj = children?.[0];
        const labelText =
          typeof labelObj === "object"
            ? labelObj?.children || "Payment Method"
            : labelObj || "Payment Method";
        const saved = children?.[1]?.children;
        const onFile = saved && typeof saved === "object" && saved.saved === true;
        return (
          <Box mt={2}>
            <Box fontSize="sm" fontWeight="500" color="gray.600" mb={1}>
              {labelText}
            </Box>
            <Box
              p={3}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              bg="gray.50"
              fontSize="sm"
              color={onFile ? "gray.800" : "gray.500"}
            >
              {onFile
                ? `Payment method on file${saved.last4 ? ` ····${saved.last4}` : ""}${saved.brand ? ` (${saved.brand})` : ""}`
                : "Payment method (added by the patient)"}
            </Box>
          </Box>
        );
      }

      if (children && !Array.isArray(children)) {
        return React.createElement(element, {key, ...props}, children);
      }
  
      const childElements =
        children?.map((child, index) =>
          renderElement({...child, key: child.key || index})
        ) || null;
  
      return  React.createElement(
            element,
            {key, ...props, readOnly: true},
            childElements
          )
    }
    
    const sections = form?.sections||[];    
    return (
      <Modal 
        isOpen={true} 
        isCentered 
        onClose={()=>close(false)} 
        size={{base: "full", md: "4xl", lg: "6xl"}}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent 
          minH={{base: "100vh", md: "90vh"}} 
          maxW={{base: "100vw", md: "90vw", lg: "80vw"}}
          display="flex"  
          flexDirection="column"  
          overflow="hidden"
        >
          <ModalCloseButton />
          <ModalHeader 
            textAlign="center" 
            color="gray.500"  
            mt="0" 
            fontSize={{base: "lg", md: "xl"}}
            fontWeight="semibold"
          >
            {form?.formTitle}
          </ModalHeader>
          <ModalBody  
            mt="0"  
            display="flex" 
            flex="1" 
            flexDirection="column"  
            overflow="hidden"  
            py={{base: 4, md: 6}}
            px={{base: 4, md: 6, lg: 8}}
          >
            <Box
              bg="gray.100"
              flex="1"
              borderWidth="1px"
              borderColor="gray.300"
              borderRadius={{base: "md", md: "lg"}}
              overflow="hidden"
              display="flex"
              flexDirection="column"
              mx="auto"
              w="100%"
              maxW={contentMaxW}
            >
              <Box
                flex="1"
                overflowY="auto"
                px={layoutPx}
                py={layoutPy}
              >
                <VStack spacing={{base: 6, md: 8}} align="stretch">
                  {sections.map((section, index) => {
                    return (
                      <Box
                        key={`section-${index}`}
                        id={`section-${index}`}
                        mt={index > 0 ? 4 : 0}
                      >
                        {section.name && (
                          <Heading
                            as="h2"
                            size={{base: "md", md: "lg"}}
                            fontWeight="semibold"
                            color="white"
                            bg="gray.400"
                            px={4}
                            py={2}
                            mb={4}
                        
                            borderBottomWidth="1px"
                            borderBottomColor="gray.300"
                          >
                            {section.name}
                          </Heading>
                        )}
                        <VStack spacing={{base: 3, md: 4}} align="stretch">
                          {section.fields.map((row, rowIdx) => {
                            // A question the form's rules hid was never asked:
                            // it is not part of what the patient sees again.
                            const asked = row.filter((element) => !isHiddenByRule(element));
                            if (row.length > 0 && asked.length === 0) return null;
                            const totalColumns = asked.length || 1;
                            const mdColumns = Math.min(2, totalColumns);
                            return (
                              <SimpleGrid
                                key={`section-${index}-row-${rowIdx}`}
                                columns={{base: 1, md: mdColumns, lg: totalColumns}}
                                spacing={{base: 3, md: 4}}
                                w="100%"
                                px={{base: 0, md: 2}}
                                mb={{base: 4, md: 6}}
                              >
                                {asked.map((element, colIdx) => {
                                  return (
                                    <Box
                                      key={`section-${index}-row-${rowIdx}-col-${colIdx}`}
                                      w="100%"
                                    >
                                      {element && renderElement(element)}
                                    </Box>
                                  );
                                })}
                              </SimpleGrid>
                            );
                          })}
                        </VStack>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }
  
  function Signature({label, signature}) {
    const {element, key, props = {}, children} = label;
  
    return (
      <Box mt={2} color="gray.700" w="100%" maxW={{base: "100%", md: "300px"}}>
        <Flex justify="space-between" align="center" mb={1}>
          {React.createElement(element, {key, ...props}, children)}
        </Flex>
        <Box
          borderRadius="md"
          display="flex"
          justifyContent="center"
          bg="white"
          alignItems="center"
          minH="100px"
          maxH="100px"
          maxW={{base: "100%", md: "250px"}}
          overflow="hidden"
          borderWidth="1px"
          borderColor="gray.500"
          p={2}
        >
          {signature && (
            <Box
              as="img"
              src={signature}
              alt="signature"
              maxH="full"
              objectFit="contain"
            />
          )}
        </Box>
      </Box>
    );
  }